// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.0.4
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../api/client'
import toast from 'react-hot-toast'
import { promptDialog } from '../components/DialogHost'
import { User, Lock, Bell, Shield, Loader2, Check, X } from 'lucide-react'

const passwordRequirements = [
  { label: '8+ characters', test: (p: string) => p.length >= 8 },
  { label: 'Uppercase', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Lowercase', test: (p: string) => /[a-z]/.test(p) },
  { label: 'Number', test: (p: string) => /\d/.test(p) },
  { label: 'Special char', test: (p: string) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(p) },
]

export default function Account() {
  const { user, refreshUser } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [isLoading, setIsLoading] = useState(false)

  // Profile
  const [profile, setProfile] = useState({
    company_name: '',
    contact_name: '',
    phone: ''
  })

  // Password
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  })

  // Notifications
  const [notifications, setNotifications] = useState({
    email_billing: true,
    email_support: true,
    email_product_updates: true,
    email_security_alerts: true
  })

  // 2FA
  const [twoFactor, setTwoFactor] = useState({
    enabled: false,
    secret: '',
    qrCodeUrl: '',
    code: ''
  })

  useEffect(() => {
    if (user) {
      setProfile({
        company_name: user.company_name || '',
        contact_name: user.contact_name || '',
        phone: ''
      })
      setTwoFactor(prev => ({ ...prev, enabled: user.two_factor_enabled }))
    }
    loadNotifications()
  }, [user])

  const loadNotifications = async () => {
    try {
      const res = await api.get('/api/portal/account/notifications')
      setNotifications(res.data)
    } catch (error) {
      console.error('Failed to load notifications:', error)
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    if (passwords.new !== passwords.confirm) {
      toast.error('Passwords do not match')
      return
    }

    const allPassed = passwordRequirements.every(req => req.test(passwords.new))
    if (!allPassed) {
      toast.error('Password does not meet requirements')
      return
    }

    setIsLoading(true)
    try {
      await api.put('/api/portal/account/password', {
        current_password: passwords.current,
        new_password: passwords.new
      })
      toast.success('Password changed')
      setPasswords({ current: '', new: '', confirm: '' })
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to change password')
    } finally {
      setIsLoading(false)
    }
  }

  const handleNotificationsSave = async () => {
    setIsLoading(true)
    try {
      await api.put('/api/portal/account/notifications', notifications)
      toast.success('Notification preferences updated')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update preferences')
    } finally {
      setIsLoading(false)
    }
  }

  const handleEnable2FA = async () => {
    setIsLoading(true)
    try {
      const res = await api.post('/api/portal/auth/enable-2fa')
      setTwoFactor(prev => ({
        ...prev,
        secret: res.data.secret,
        qrCodeUrl: res.data.qr_code_url
      }))
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to enable 2FA')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await api.post('/api/portal/auth/verify-2fa', { code: twoFactor.code })
      toast.success('Two-factor authentication enabled')
      setTwoFactor(prev => ({ ...prev, enabled: true, secret: '', qrCodeUrl: '', code: '' }))
      refreshUser()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Invalid code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisable2FA = async () => {
    const code = await promptDialog({
      title: 'Disable two-factor authentication',
      message: 'Enter your 2FA code to disable:',
      placeholder: '6-digit code',
      confirmText: 'Disable',
      danger: true,
    })
    if (!code) return

    setIsLoading(true)
    try {
      await api.post('/api/portal/auth/disable-2fa', { code })
      toast.success('Two-factor authentication disabled')
      setTwoFactor(prev => ({ ...prev, enabled: false }))
      refreshUser()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Invalid code')
    } finally {
      setIsLoading(false)
    }
  }

  const tabs = [
    { id: 'profile', icon: User, label: 'Profile' },
    { id: 'security', icon: Lock, label: 'Security' },
    { id: 'notifications', icon: Bell, label: 'Notifications' },
  ]

  return (
    <div className="space-y-6 fade-in">
      <h1 className="text-2xl font-bold">Account Settings</h1>

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-48 shrink-0">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-500/10 text-primary-500'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 max-w-2xl">
          {activeTab === 'profile' && (
            <div className="card space-y-6">
              <h2 className="text-lg font-semibold">Profile Information</h2>
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-400">
                To update your profile information, please contact support.
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email</label>
                  <p className="text-white font-medium">{user?.email || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Company Name</label>
                  <p className="text-white font-medium">{profile.company_name || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contact Name</label>
                  <p className="text-white font-medium">{profile.contact_name || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* Change Password */}
              <div className="card">
                <h2 className="text-lg font-semibold mb-4">Change Password</h2>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Current Password</label>
                    <input
                      type="password"
                      value={passwords.current}
                      onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">New Password</label>
                    <input
                      type="password"
                      value={passwords.new}
                      onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                      required
                    />
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {passwordRequirements.map((req, i) => (
                        <div
                          key={i}
                          className={`flex items-center gap-1 text-xs ${
                            req.test(passwords.new) ? 'text-green-500' : 'text-gray-500'
                          }`}
                        >
                          {req.test(passwords.new) ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {req.label}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                    <input
                      type="password"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                      required
                    />
                  </div>
                  <button type="submit" disabled={isLoading} className="btn btn-primary">
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Change Password
                  </button>
                </form>
              </div>

              {/* Two-Factor Authentication */}
              <div className="card">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
                    <p className="text-sm text-gray-400">Add an extra layer of security</p>
                  </div>
                  <span className={`badge ${twoFactor.enabled ? 'badge-success' : 'badge-warning'}`}>
                    {twoFactor.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>

                {twoFactor.enabled ? (
                  <button onClick={handleDisable2FA} disabled={isLoading} className="btn btn-danger">
                    Disable 2FA
                  </button>
                ) : twoFactor.qrCodeUrl ? (
                  <form onSubmit={handleVerify2FA} className="space-y-4">
                    <div className="flex flex-col items-center gap-4 p-4 bg-gray-800 rounded-lg">
                      <img src={twoFactor.qrCodeUrl} alt="QR Code" className="w-48 h-48 bg-white p-2 rounded" />
                      <p className="text-sm text-gray-400">
                        Scan this QR code with your authenticator app
                      </p>
                      <code className="text-xs text-gray-500">{twoFactor.secret}</code>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Verification Code</label>
                      <input
                        type="text"
                        value={twoFactor.code}
                        onChange={(e) => setTwoFactor({ ...twoFactor, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        className="text-center text-xl tracking-widest"
                        required
                      />
                    </div>
                    <button type="submit" disabled={isLoading} className="btn btn-primary">
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Verify & Enable
                    </button>
                  </form>
                ) : (
                  <button onClick={handleEnable2FA} disabled={isLoading} className="btn btn-primary">
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                    Enable 2FA
                  </button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Email Notifications</h2>
              <div className="space-y-4">
                {[
                  { key: 'email_billing', label: 'Billing & Invoices', desc: 'Payment confirmations and invoices' },
                  { key: 'email_support', label: 'Support Updates', desc: 'Ticket replies and updates' },
                  { key: 'email_product_updates', label: 'Product Updates', desc: 'New features and releases' },
                  { key: 'email_security_alerts', label: 'Security Alerts', desc: 'Important security notifications' }
                ].map((item) => (
                  <label key={item.key} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg cursor-pointer">
                    <div>
                      <p className="font-medium">{item.label}</p>
                      <p className="text-sm text-gray-400">{item.desc}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={notifications[item.key as keyof typeof notifications]}
                      onChange={(e) => setNotifications({ ...notifications, [item.key]: e.target.checked })}
                      className="w-5 h-5 rounded"
                    />
                  </label>
                ))}
              </div>
              <button onClick={handleNotificationsSave} disabled={isLoading} className="btn btn-primary mt-6">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save Preferences
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
